/**
 * Environment identity and the startup guard.
 *
 * See CLAUDE.md 3.4 and 10, OPERATIONS.md section 2, SECURITY.md section 8.
 *
 * Three jobs:
 *  1. Tell every screen which environment it is, so the badge is never wrong.
 *  2. Refuse to boot if a development process is holding production credentials.
 *  3. Say where this API is reachable from the outside, so the OAuth redirect URI has exactly
 *     one definition — the API route, the token exchange, and the value the owner registers on
 *     the Intuit app must all be the same string.
 */
import { z } from 'zod';
/**
 * Two environments, not three. ADR-0006 (2026-09-17): staging was retired because a one-person
 * organisation gets no oversight from a third environment it does not have time to look at, and
 * every phase before the Phase 9 cutover runs on sample data behind feature flags anyway. The
 * deployed Render group is production from now on; until the cutover it carries the SAMPLE DATA
 * badge, which is what makes that honest (CLAUDE.md 3.4).
 */
export const ENVIRONMENTS = ['development', 'production'];
export const environmentSchema = z.enum(ENVIRONMENTS);
export function environmentBadge(ctx) {
    return {
        environment: ctx.environment,
        label: ctx.environment.toUpperCase(),
        sampleData: ctx.sampleData,
        sampleDataLabel: ctx.sampleData
            ? ctx.environment === 'development'
                ? 'DEVELOPMENT — MOCK CONNECTORS'
                : 'SAMPLE DATA'
            : null,
    };
}
/**
 * Connector credential variables that must never be present outside production.
 *
 * Deliberately a denylist of real-credential variables rather than a prefix rule: a prefix rule
 * quietly stops matching the day someone names a variable differently.
 */
export const PRODUCTION_CREDENTIAL_VARS = [
    'GUESTY_CLIENT_SECRET',
    'GUESTY_ACCESS_TOKEN',
    'QBO_CLIENT_SECRET',
    'QBO_REFRESH_TOKEN',
    'PLAID_SECRET',
    'PLAID_ACCESS_TOKEN',
    'STRIPE_SECRET_KEY',
    'GOOGLE_CLIENT_SECRET',
    'GOOGLE_REFRESH_TOKEN',
];
export class ProductionCredentialInDevelopmentError extends Error {
    offendingVars;
    constructor(offendingVars) {
        super(`Refusing to boot: SIXGEN_ENV is not production but production connector credentials are ` +
            `present (${offendingVars.join(', ')}). Production credentials must exist only in the ` +
            `production environment (SECURITY.md 8). Remove them from this environment's secrets.`);
        this.offendingVars = offendingVars;
        this.name = 'ProductionCredentialInDevelopmentError';
    }
}
/**
 * The startup guard. Phase 1 Definition of Done.
 *
 * Throws — it does not warn. A process that boots with the wrong credentials is exactly the
 * failure this is meant to prevent, so it must be impossible to ignore.
 */
export function assertNoProductionCredentials(environment, env = process.env) {
    if (environment === 'production')
        return;
    const offending = PRODUCTION_CREDENTIAL_VARS.filter((name) => {
        const value = env[name];
        return typeof value === 'string' && value.trim() !== '';
    });
    if (offending.length > 0) {
        throw new ProductionCredentialInDevelopmentError([...offending]);
    }
}
/**
 * Refuse to write to the production database from a test or a local script.
 *
 * It holds real data, and the append-only tables (`raw.*`, `sys.event`, `sys.audit_log`) mean a
 * mistake there cannot be undone. Tests write real rows; the sync and detect scripts write raw
 * payloads, core rows, and audit rows. Neither has any business touching that environment, and
 * a scheduled worker job — the production path — never calls this. Neither has any business
 * touching that environment.
 *
 * Lived in test/db.ts until plan 9 (Task 3), when the scripts needed the same guard.
 */
export function assertSafeTarget(env = process.env) {
    const environment = env.SIXGEN_ENV ?? 'development';
    if (environment === 'production') {
        throw new Error(`Refusing to run against a database with SIXGEN_ENV=${environment}. Tests and local ` +
            'scripts write real rows, and the raw, event, and audit tables cannot be cleaned up ' +
            'afterwards. Point DATABASE_URL or TEST_DATABASE_URL at a throwaway database instead.');
    }
}
export function readEnvironment(env = process.env) {
    const parsed = environmentSchema.safeParse(env.SIXGEN_ENV ?? 'development');
    if (!parsed.success) {
        throw new Error(`SIXGEN_ENV must be one of ${ENVIRONMENTS.join(' | ')}; got ${JSON.stringify(env.SIXGEN_ENV)}`);
    }
    return parsed.data;
}
/**
 * Where this API is reachable from the OUTSIDE — from a browser, and from Intuit.
 *
 * There has to be exactly one answer to this, because the same string appears in three places
 * that must agree character for character or the OAuth flow fails with a message from Intuit
 * that explains nothing: the `redirect_uri` on the authorize URL, the `redirect_uri` on the
 * token exchange, and the redirect URI the owner registers on the Intuit app itself. So it is
 * derived here, printed by `pnpm qbo:status`, and shown on the Connections screen — the owner
 * copies it out of the application rather than being told it in a chat message that may be
 * wrong by the time they read it.
 *
 * The order, and why:
 *   1. `PUBLIC_API_URL` when set — the explicit answer always wins.
 *   2. `http://localhost:3001` in development. Vite serves the SPA on 5173 and proxies `/api`
 *      here, but INTUIT is not going through Vite: it redirects the browser straight at the API,
 *      so the API's own port is the right default and `WEB_PUBLIC_URL` would be the wrong one.
 *   3. `WEB_PUBLIC_URL` otherwise. Staging and production serve the SPA from this same process
 *      on this same origin (`apps/api/src/server.ts` says why), so the app's public URL IS the
 *      API's, and requiring a second variable that must always equal the first is a way to have
 *      them disagree.
 *   4. Otherwise it throws. A deployment with neither variable cannot produce a redirect URI,
 *      and guessing one would produce an Intuit error rather than a missing-configuration one.
 *
 * A trailing slash is trimmed, because `${base}${path}` is how the URI is built and
 * `https://host//api/...` is not the string the owner registered.
 */
export const PUBLIC_API_URL_VARIABLE = 'PUBLIC_API_URL';
/** The development default. The API's own port, not Vite's — Intuit does not use the proxy. */
export const DEVELOPMENT_API_URL = 'http://localhost:3001';
export function readPublicApiUrl(env = process.env) {
    const explicit = env[PUBLIC_API_URL_VARIABLE]?.trim();
    if (explicit)
        return explicit.replace(/\/+$/, '');
    if (readEnvironment(env) === 'development')
        return DEVELOPMENT_API_URL;
    const web = env.WEB_PUBLIC_URL?.trim();
    if (web)
        return web.replace(/\/+$/, '');
    throw new Error(`${PUBLIC_API_URL_VARIABLE} is not set and there is no WEB_PUBLIC_URL to fall back on, so ` +
        'the OAuth redirect URI cannot be derived. Set it to the origin this API is reachable at ' +
        'from a browser — the same origin you register as a redirect URI on the Intuit app.');
}
/** The path Intuit redirects to. One constant, so the route and the URI cannot drift apart. */
export const QBO_REDIRECT_PATH = '/api/credentials/qbo/callback';
/**
 * The exact redirect URI to register on the Intuit app (Keys & credentials → Redirect URIs).
 *
 * Intuit compares this string EXACTLY. A missing port, an http where the app says https, or a
 * trailing slash is a failed authorization with an unhelpful message, which is why this is
 * computed rather than typed anywhere.
 */
export function qboRedirectUri(env = process.env) {
    return `${readPublicApiUrl(env)}${QBO_REDIRECT_PATH}`;
}
export function readEnvironmentContext(env = process.env) {
    const environment = readEnvironment(env);
    assertNoProductionCredentials(environment, env);
    return {
        environment,
        sampleData: env.SIXGEN_SAMPLE_DATA === 'true',
    };
}
//# sourceMappingURL=env.js.map